import { Container, Sprite, Texture } from "pixi.js";
import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity } from "../../../core/entity/Entity";
import { GameSprite } from "../../../core/entity/GameSprite";
import { on } from "../../../core/entity/handler";
import { Vector } from "../../../game/Vectors";
import { cellToPixel, inchesToPixels } from "../../../views/shop-scale";
import { ACTORS_Z } from "../../../views/z-order";
import { TRIP_TRANSITIONS_DISABLED } from "../TripTheater";

/**
 * Wife, on the shop floor for the length of a scene: a walker with no
 * sim entity behind her, spawned by the CutsceneDirector when a scene's
 * script says she enters and destroyed when it says she leaves. Where
 * she comes from is never seen — she starts off the bottom of the
 * indoor framing, beside the parked truck, and walks the same route a
 * customer would if one ever came up the driveway: the grass strip
 * past the truck, the doorway, then straight to whatever she came to
 * look at.
 *
 * The walk runs on real seconds, not shop minutes (the clock is
 * stopped for the scene), and the E2E build skips it the way it skips
 * the truck's rolls. The body is the player's own texture in pink
 * for now — generated first, swapped later (docs/asset-backlog.md).
 */

const PERSON_SIZE = inchesToPixels(32);
/** Her stroll, in cells per real second — brisk, because a scene has
 * to stay under half a minute and the walk in is the boring half. */
const WALK_SPEED = 4.5;
const TURN_RATE = 12;
const WIFE_TINT = 0xf2a0c4;

export class WifeView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Level;

  private root: Container & GameSprite;
  private body: Sprite;
  /** Continuous position, in cell coordinates. */
  position: Vector;
  private heading = -Math.PI / 2;
  /** The route in, spawn point first. */
  private readonly route: ReadonlyArray<Vector>;
  /** The waypoint being walked toward, as an index into `path`. */
  private path: ReadonlyArray<Vector>;
  private next = 1;
  /** Whether the current path has been walked to its end. */
  private done = false;

  constructor(route: ReadonlyArray<Vector>) {
    super();
    if (route.length < 2) {
      throw new Error("A walk needs a start and an end");
    }
    this.route = route;
    this.path = route;
    this.position = [...route[0]] as Vector;

    this.root = new Container() as Container & GameSprite;
    this.root.layerName = "actors";
    this.root.zIndex = ACTORS_Z.wife;
    this.body = this.root.addChild(
      new Sprite(Texture.from("/images/person.png")),
    );
    this.body.anchor.set(0.5);
    this.body.width = PERSON_SIZE;
    this.body.height = PERSON_SIZE;
    this.body.tint = WIFE_TINT;
    this.sprite = this.root;

    if (TRIP_TRANSITIONS_DISABLED) this.finishWalk();
  }

  /** Whether she has reached the end of the current walk. */
  get arrived(): boolean {
    return this.done;
  }

  /** Turn around and walk back out the way she came. */
  leave(): void {
    this.path = [...this.route].reverse();
    // Start from wherever she stands, toward the last waypoint she
    // passed on the way in.
    this.path = [this.position, ...this.path.slice(1)];
    this.next = 1;
    this.done = false;
    if (TRIP_TRANSITIONS_DISABLED) this.finishWalk();
  }

  private finishWalk(): void {
    this.position = [...this.path[this.path.length - 1]] as Vector;
    this.next = this.path.length;
    this.done = true;
  }

  @on("tick")
  onTick(dt: number) {
    if (this.done) return;
    let budget = WALK_SPEED * dt;
    while (budget > 0 && this.next < this.path.length) {
      const target = this.path[this.next];
      const dx = target[0] - this.position[0];
      const dy = target[1] - this.position[1];
      const distance = Math.hypot(dx, dy);
      if (distance <= budget) {
        this.position = [...target] as Vector;
        budget -= distance;
        this.next += 1;
        continue;
      }
      this.heading = Math.atan2(dy, dx);
      this.position = [
        this.position[0] + (dx / distance) * budget,
        this.position[1] + (dy / distance) * budget,
      ];
      budget = 0;
    }
    if (this.next >= this.path.length) this.done = true;
  }

  @on("render")
  onRender(dt: number) {
    this.root.x = cellToPixel(this.position[0]);
    this.root.y = cellToPixel(this.position[1]);
    // Ease around toward the heading, the way the player's body does;
    // the texture faces up, so +x is a quarter turn clockwise.
    const target = this.heading + Math.PI / 2;
    const delta =
      ((target - this.root.rotation + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
    this.root.rotation += delta * Math.min(1, dt * TURN_RATE);
  }
}
